/**
 * SubstrateStatusBadge Component
 *
 * Displays substrate batch status with appropriate styling.
 * Part of US-P2-003: Substrate Production & Block Creation
 */

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

const STATUS_CONFIG = {
  MIXING: {
    label: 'Mixing',
    variant: 'outline',
    className: 'border-odin-blue text-odin-blue bg-odin-blue/10'
  },
  BAGGED: {
    label: 'Bagged',
    variant: 'outline',
    className: 'border-purple-500 text-purple-500 bg-purple-500/10'
  },
  HEAT_CYCLING: {
    label: 'Heat Cycling',
    variant: 'outline',
    className: 'border-odin-red text-odin-red bg-odin-red/10'
  },
  COOLING: {
    label: 'Cooling',
    variant: 'outline',
    className: 'border-amber-500 text-amber-500 bg-amber-500/10'
  },
  READY: {
    label: 'Ready',
    variant: 'outline',
    className: 'border-odin-green text-odin-green bg-odin-green/10'
  },
  DEPLETED: {
    label: 'Depleted',
    variant: 'outline',
    className: 'border-content-tertiary text-content-tertiary bg-surface-secondary'
  }
}

export default function SubstrateStatusBadge({ status, className }) {
  const config = STATUS_CONFIG[status] || {
    label: status,
    variant: 'outline',
    className: ''
  }

  return (
    <Badge
      variant={config.variant}
      className={cn(config.className, className)}
    >
      {config.label}
    </Badge>
  )
}

/**
 * Get status configuration for external use
 * @param {string} status - The status to get config for
 * @returns {Object} Status configuration
 */
export function getStatusConfig(status) {
  return STATUS_CONFIG[status] || {
    label: status,
    variant: 'outline',
    className: ''
  }
}

/**
 * Get next status in workflow
 * @param {string} currentStatus - Current status
 * @returns {string|null} Next status or null if at end
 */
export function getNextStatus(currentStatus) {
  const STATUS_ORDER = ['MIXING', 'BAGGED', 'HEAT_CYCLING', 'COOLING', 'READY', 'DEPLETED']
  const currentIndex = STATUS_ORDER.indexOf(currentStatus)
  if (currentIndex === -1 || currentIndex >= STATUS_ORDER.length - 1) {
    return null
  }
  return STATUS_ORDER[currentIndex + 1]
}
